import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EChartsOption } from 'echarts';
import { AuthService } from '../../../services/auth.service';
import { DashboardService } from '../../../services/dashboard.service';
import { NeighborhoodService } from '../../../services/neighborhood.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import 'moment/locale/es';
import { VisitTodaySummary } from '../../../models/visit-today-summary.model';
import { ReportLatest, ReportMonthlySummary } from '../../../models/report-insights.model';
import { ReportService } from '../../../services/report.service';

export interface IVisitsTypeSummary {
  name: string;
  count: number;
  percentage: string;
  color: string;
}

export interface IVisitsHourlyRow {
  hour: string;
  counts: number[];
  total: number;
}

/** Ítem normalizado de una gráfica de composición (dona o barra). */
export interface IChartItemSummary {
  name: string;
  value: number;
  percentage: string;
  color: string;
}

/** Sentido de la variación mensual. Siempre acompañado de texto e icono. */
export type MonthVariationDirection = 'up' | 'down' | 'flat' | 'none';

/** Fila ya normalizada de la lista de últimos reportes. */
export interface IReportListItem {
  reportId: string;
  title: string;
  statusName: string;
  statusIcon: string;
  statusClass: string;
  locationLabel: string;
  dateLabel: string;
  dateTime: string;
}


@Component({
  selector: 'app-neighborhood-page',
  templateUrl: './neighborhood-page.component.html',
  styleUrl: './neighborhood-page.component.scss'
})
export class NeighborhoodPageComponent {

  public model?: NgbDateStruct;
  public date?: { year: number; month: number };

  /* ---------------------------- ANCHOR VISITS UI ---------------------------- */

  /** Opciones de la gráfica de visitas por hora del día seleccionado. */
  public visitsTodaySummaryChartOptions?: EChartsOption;

  /** Fecha seleccionada en formato largo legible, ej. "Sábado 22 de agosto de 2026". */
  public selectedDateLabel: string = '';

  /** Total de visitas del día seleccionado. */
  public totalVisits: number = 0;

  /** Desglose de visitas por tipo con el color de su serie en la gráfica. */
  public visitTypeSummaries: IVisitsTypeSummary[] = [];

  /** Hora con mayor número de visitas del día seleccionado. */
  public peakHourLabel?: string;
  public peakHourCount: number = 0;

  /** Descripción textual de la gráfica para lectores de pantalla. */
  public chartAriaLabel: string = '';

  /** Alternativa tabular de la gráfica (visually-hidden) para accesibilidad. */
  public hourlyTableRows: IVisitsHourlyRow[] = [];

  /** Paleta accesible (contraste >= 4.5:1 sobre blanco) para tipos no mapeados. */
  private readonly seriesColorPalette: string[] = [
    '#01579B',
    '#0E7C66',
    '#B45309',
    '#6D28D9',
    '#B91C1C',
    '#0F766E'
  ];

  /** Color fijo por tipo de visita conocido (clave normalizada sin acentos). */
  private readonly seriesColorByType: Record<string, string> = {
    'a pie': '#01579B',
    'en vehiculo': '#0E7C66',
    'servicio': '#B45309'
  };

  private readonly numberFormatter = new Intl.NumberFormat('es-MX');

  /* --------------------------- ANCHOR HOUSES UI --------------------------- */

  /** Cifras del padrón de predios mostradas como tarjetas KPI. */
  public housesTotal: number = 0;
  public housesActive: number = 0;
  public housesOverdue: number = 0;

  /** Peso porcentual de cada estado sobre el total de predios. */
  public housesActivePercentage: string = '0.0';
  public housesOverduePercentage: string = '0.0';

  /** Composición del padrón, también usada por la tabla accesible. */
  public housesDistribution: IChartItemSummary[] = [];
  public hasHousesData: boolean = false;
  public housesChartAriaLabel: string = '';

  /* ------------------------ ANCHOR CURRENT MONTH UI ----------------------- */

  /** Visitas acumuladas del mes actual y del mes anterior. */
  public visitsCurrentMonth: number = 0;
  public visitsLastMonth: number = 0;

  /** Etiquetas legibles de ambos meses, ej. "Agosto de 2026". */
  public currentMonthLabel: string = '';
  public lastMonthLabel: string = '';

  /** Variación porcentual del mes actual contra el anterior. */
  public monthVariationValue: string = '—';
  public monthVariationText: string = 'Sin datos del mes anterior';
  public monthVariationDirection: MonthVariationDirection = 'none';
  public monthVariationIcon: string = 'remove';

  /* --------------------- ANCHOR MONTH COMPOSITION UI ---------------------- */

  /** Tipos de visita del mes con su color, total y tabla accesible. */
  public visitTypesMonthSummaries: IChartItemSummary[] = [];
  public visitTypesMonthTotal: number = 0;
  public hasVisitTypesData: boolean = false;
  public visitTypesChartAriaLabel: string = '';

  /** Servicios del mes con su color, total y tabla accesible. */
  public servicesMonthSummaries: IChartItemSummary[] = [];
  public servicesMonthTotal: number = 0;
  public hasServicesData: boolean = false;
  public servicesChartAriaLabel: string = '';
  public servicesPieChartAriaLabel: string = '';

  /* ---------------------------- ANCHOR REPORTS UI ------------------------- */

  /** Cifras del endpoint de insights de reportes mostradas como tarjetas KPI. */
  public reportsTotal: number = 0;
  public reportsPendings: number = 0;
  public reportsInProgress: number = 0;
  public reportsResolved: number = 0;
  public reportsCanceled: number = 0;

  /** Reportes sin cerrar: pendientes mas los que estan en progreso. Los
      cancelados NO cuentan: un reporte cancelado ya está cerrado. */
  public get reportsOpen(): number {
    return this.reportsPendings + this.reportsInProgress;
  }

  /** Peso porcentual de cada estado sobre el total de reportes. */
  public reportsPendingsPercentage: string = '0.0';
  public reportsInProgressPercentage: string = '0.0';
  public reportsResolvedPercentage: string = '0.0';
  public reportsCanceledPercentage: string = '0.0';

  /** Tiempo promedio de resolución ya formateado (horas o días). */
  public reportsAverageResolutionLabel: string = 'Sin resoluciones registradas';

  /** Composición por estado, también usada por la tabla accesible de la dona. */
  public reportsDistribution: IChartItemSummary[] = [];
  public hasReportsData: boolean = false;
  public reportsDonutAriaLabel: string = '';

  /** Serie mensual de los últimos 6 meses y su tabla accesible. */
  public reportsMonthlyRows: ReportMonthlySummary[] = [];
  public hasReportsMonthlyData: boolean = false;
  public reportsMonthlyAriaLabel: string = '';

  /** Variación del mes en curso contra el anterior, en reportes creados. */
  public reportsMonthTotal: number = 0;
  public reportsMonthVariationValue: string = '—';
  public reportsMonthVariationText: string = 'Sin reportes del mes anterior';
  public reportsMonthVariationDirection: MonthVariationDirection = 'none';
  public reportsMonthVariationIcon: string = 'remove';

  /** Últimos 5 reportes ya normalizados para pintarse sin lógica en el HTML. */
  public latestReports: IReportListItem[] = [];
  public hasLatestReports: boolean = false;

  /** Colores accesibles por estado de reporte (contraste >= 4.5:1 sobre blanco). */
  private readonly reportPendingColor: string = '#B45309';
  private readonly reportProgressColor: string = '#01579B';
  private readonly reportResolvedColor: string = '#0E7C66';
  /* Cancelado: gris pizarra neutro. No compite con los tres colores de estado
     —no es una alerta ni un logro, es una baja— y conserva 7.5:1 sobre blanco. */
  private readonly reportCanceledColor: string = '#475569';

  reportsStatusDonutOption: EChartsOption = {};

  reportsMonthlyOption: EChartsOption = {};

  /* ------------------------- ANCHOR CHART TOKENS -------------------------- */

  /** Tipografía heredada del layout para que las gráficas no rompan la escala. */
  private readonly chartTextStyle = {
    fontFamily: 'inherit',
    color: '#334155'
  };

  /** Mismo tooltip (fondo, borde y sombra) en todas las gráficas de la página. */
  private readonly chartTooltipStyle = {
    borderColor: '#E2E8F0',
    borderWidth: 1,
    backgroundColor: '#FFFFFF',
    extraCssText: 'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;',
    textStyle: {
      color: '#0F172A',
      fontSize: 12
    }
  };

  /** Misma leyenda inferior con marcador circular en todas las gráficas. */
  private readonly chartLegendStyle = {
    bottom: 0,
    icon: 'circle',
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 20,
    textStyle: {
      fontSize: 12,
      color: '#334155'
    }
  };

  /** Colores accesibles del estado del padrón (contraste >= 4.5:1 sobre blanco). */
  private readonly positiveColor: string = '#0E7C66';
  private readonly negativeColor: string = '#B91C1C';

  servicesSummary: EChartsOption = {};

  housesFlatsSummaryOption: EChartsOption = {};

  visitTypeSummaryChart: EChartsOption = {};

  serviceSummaryPieChart: EChartsOption = {};

  public neighborhoodId?: string;
  
  constructor(
    private activatedRoute: ActivatedRoute,
    public authService: AuthService,
    public dashboardService: DashboardService,
    public neighborhoodService: NeighborhoodService,
    public reportService: ReportService,
    // private modalService: NgbModal,
    // public spinnerService: SpinnerService
  ) {
    this.activatedRoute.params.subscribe(
      async params => {
        this.neighborhoodId = params['neighborhoodId'];
        await Promise.all([
          this.dashboardService.getAllAboutNeighborhood(
            this.neighborhoodId!
          ),
          this.reportService.getInsights(
            this.neighborhoodId!
          )
        ]);
        this.updateCHarts();
      }
    );
  }

  ngOnInit() {

    const momentDate = moment.utc().add(
      this.visitsTimeZone,
      'hours'
    );

    this.model = {
      year: momentDate.year(),
      month: momentDate.month() + 1,
      day: momentDate.date(),
    };

    this.setSelectedDateLabel();

    // Las etiquetas de mes se muestran incluso mientras la petición está en curso.
    this.setMonthLabels();

  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR getData                               */
  /* -------------------------------------------------------------------------- */
  // region getData
  async getData() {

    this.setSelectedDateLabel();

    const date = this.model
      ? moment({
          year: this.model.year,
          month: this.model.month - 1,
          day: this.model.day
        }).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');

    console.log(date);
    
    await Promise.all([
      this.dashboardService.getAllAboutNeighborhood(
        this.neighborhoodId!,
        date
      ),
      this.reportService.getInsights(
        this.neighborhoodId!
      )
    ]);
    this.updateCHarts();
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR hasVisits                              */
  /* -------------------------------------------------------------------------- */
  // region hasVisits
  get hasVisits(): boolean {
    return this.totalVisits > 0;
  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR visitsTimeZone                           */
  /* -------------------------------------------------------------------------- */
  // region visitsTimeZone
  /** Offset horario de la colonia. Mismo fallback que se usa al armar la fecha. */
  get visitsTimeZone(): number {
    return this.neighborhoodService.currentNeighborhood?.timeZone ?? 0;
  }

  async moveDay(
    increment: number
  ) {

    const movedDate = this.model
      ? moment({
          year: this.model.year,
          month: this.model.month - 1,
          day: this.model.day
        }).add(increment, 'days')
      : moment().add(increment, 'days');

    this.model = {
      year: movedDate.year(),
      month: movedDate.month() + 1,
      day: movedDate.date()
    };

    await this.getData();
  }

  updateCHarts() {

    this.updateVisisitsTodaySummary();

    /* Primero el resumen mensual: deja listas las etiquetas de mes que usan las
       descripciones accesibles de las demás gráficas. */
    this.updateCurrentMonthSummary();
    this.updateServicesSummary();
    this.updateVisitTypeSummary();
    this.updateHousesFlatsSummary();
    this.updateReportsSummary();

  }

  /* -------------------------------------------------------------------------- */
  /*                     ANCHOR updateVisisitsTodaySummary                      */
  /* -------------------------------------------------------------------------- */
  // region updateVisisitsTodaySummary
  updateVisisitsTodaySummary() {

    const visitsTodaySummary = this.dashboardService.dashboard?.visitsTodaySummary ?? [];

    if (!visitsTodaySummary.length) {
      this.resetVisitsSummaries();
      return;
    }

    const dataLabels = visitsTodaySummary.map(item => item.hourLabel);

    // Recolectar todos los tipos únicos
    const typeSet = new Set<string>();
    for (const hourData of visitsTodaySummary) {
      for (const type of hourData.types) {
        typeSet.add(type.name);
      }
    }
    const types = Array.from(typeSet).sort();

    const colors = types.map(
      (typeName, index) => this.getSeriesColor(typeName, index)
    );

    this.setVisitsSummaries(visitsTodaySummary, types, colors);

    // Construir series para ECharts
    const series = types.map((typeName, index) => ({
      name: typeName,
      type: 'bar',
      stack: 'total',
      barMaxWidth: 22,
      itemStyle: index === types.length - 1
        ? { borderRadius: [3, 3, 0, 0] }
        : {},
      label: {
        show: true,
        position: 'inside',
        fontSize: 10,
        fontWeight: 600,
        color: '#FFFFFF',
        // Oculta los data labels de las barras vacías para eliminar ruido visual.
        formatter: (params: any) => params.value > 0
          ? this.numberFormatter.format(params.value)
          : ''
      },
      labelLayout: { hideOverlap: true },
      emphasis: { focus: 'series' },
      data: visitsTodaySummary.map(hourData => {
        const found = hourData.types.find(item => item.name === typeName);
        return found ? found.count : 0;
      })
    }));

    this.visitsTodaySummaryChartOptions = {
      color: colors,
      aria: { enabled: true },
      animationDuration: 300,
      textStyle: {
        fontFamily: 'inherit',
        color: '#334155'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        borderColor: '#E2E8F0',
        borderWidth: 1,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;',
        textStyle: {
          color: '#0F172A',
          fontSize: 12
        },
        formatter: (params: any) => this.buildVisitsTooltip(params)
      },
      legend: {
        bottom: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 20,
        textStyle: {
          fontSize: 12,
          color: '#334155'
        }
      },
      grid: {
        top: 16,
        left: 8,
        right: 8,
        bottom: 34,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dataLabels,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        axisLabel: {
          color: '#475569',
          fontSize: 11,
          hideOverlap: true
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#475569',
          fontSize: 11
        },
        splitLine: {
          lineStyle: {
            color: '#E2E8F0',
            type: 'dashed'
          }
        }
      },
      series: series as any
    };
  }

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR updateServicesSummary                        */
  /* -------------------------------------------------------------------------- */
  // region updateServicesSummary
  updateServicesSummary() {

    const servicesSummary = this.dashboardService.dashboard?.servicesSummary ?? [];

    this.servicesMonthTotal = servicesSummary.reduce(
      (accumulated, item) => accumulated + item.visitCount,
      0
    );

    this.servicesMonthSummaries = servicesSummary.map(
      (item, index) => ({
        name: item.service,
        value: item.visitCount,
        percentage: this.servicesMonthTotal
          ? ((item.visitCount / this.servicesMonthTotal) * 100).toFixed(1)
          : '0.0',
        color: this.getSeriesColor(item.service, index)
      })
    );

    this.hasServicesData = this.servicesMonthTotal > 0;

    if (!this.hasServicesData) {
      this.servicesSummary = {};
      this.serviceSummaryPieChart = {};
      this.servicesChartAriaLabel = '';
      this.servicesPieChartAriaLabel = '';
      return;
    }

    const breakdown = this.servicesMonthSummaries
      .map(item => `${item.name}: ${item.value}`)
      .join(', ');

    this.servicesChartAriaLabel = `Gráfica de barras con las entradas por servicio de ${this.currentMonthLabel}. `
      + `Total ${this.servicesMonthTotal} entradas. ${breakdown}.`;

    this.servicesPieChartAriaLabel = `Gráfica de dona con la distribución de servicios de ${this.currentMonthLabel}. `
      + `Total ${this.servicesMonthTotal} entradas. ${breakdown}.`;

    /* ------------------------- BARRAS HORIZONTALES ------------------------- */
    this.servicesSummary = {
      color: this.servicesMonthSummaries.map(item => item.color),
      aria: { enabled: true },
      animationDuration: 300,
      textStyle: this.chartTextStyle,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...this.chartTooltipStyle,
        formatter: (params: any) => this.buildAxisTooltip(
          params,
          this.servicesMonthTotal
        )
      },
      grid: {
        top: 16,
        left: 8,
        right: 24,
        bottom: 8,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#475569',
          fontSize: 11
        },
        splitLine: {
          lineStyle: {
            color: '#E2E8F0',
            type: 'dashed'
          }
        }
      },
      yAxis: {
        type: 'category',
        data: this.servicesMonthSummaries.map(item => item.name),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        axisLabel: {
          color: '#475569',
          fontSize: 11,
          hideOverlap: true
        }
      },
      series: [
        {
          name: 'Entradas',
          type: 'bar',
          barMaxWidth: 22,
          itemStyle: { borderRadius: [0, 3, 3, 0] },
          emphasis: { focus: 'series' },
          label: {
            show: true,
            position: 'right',
            color: '#0F172A',
            fontSize: 11,
            fontWeight: 600,
            formatter: (params: any) => params.value > 0
              ? this.numberFormatter.format(params.value)
              : ''
          },
          labelLayout: { hideOverlap: true },
          data: this.servicesMonthSummaries.map(item => item.value)
        }
      ]
    };

    /* ------------------------------- DONA ---------------------------------- */
    this.serviceSummaryPieChart = this.buildDonutOptions(
      this.servicesMonthSummaries,
      'Servicio'
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                       ANCHOR updateVisitTypeSummary                        */
  /* -------------------------------------------------------------------------- */
  // region updateVisitTypeSummary
  updateVisitTypeSummary() {

    const visitTypesSummary = this.dashboardService.dashboard?.visitTypesSummary ?? [];

    this.visitTypesMonthTotal = visitTypesSummary.reduce(
      (accumulated, item) => accumulated + item.visitCount,
      0
    );

    /* Mismo mapa de color por tipo normalizado y mismo fallback por índice que la
       gráfica apilada de "Visitas por hora": un tipo conserva su color en toda la
       pantalla. */
    this.visitTypesMonthSummaries = visitTypesSummary.map(
      (item, index) => ({
        name: item.visitTypeDescription,
        value: item.visitCount,
        percentage: this.visitTypesMonthTotal
          ? ((item.visitCount / this.visitTypesMonthTotal) * 100).toFixed(1)
          : '0.0',
        color: this.getSeriesColor(item.visitTypeDescription, index)
      })
    );

    this.hasVisitTypesData = this.visitTypesMonthTotal > 0;

    if (!this.hasVisitTypesData) {
      this.visitTypeSummaryChart = {};
      this.visitTypesChartAriaLabel = '';
      return;
    }

    const breakdown = this.visitTypesMonthSummaries
      .map(item => `${item.name}: ${item.value}`)
      .join(', ');

    this.visitTypesChartAriaLabel = `Gráfica de dona con los tipos de visita de ${this.currentMonthLabel}. `
      + `Total ${this.visitTypesMonthTotal} visitas. ${breakdown}.`;

    this.visitTypeSummaryChart = this.buildDonutOptions(
      this.visitTypesMonthSummaries,
      'Tipo de visita'
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                      ANCHOR updateHousesFlatsSummary                       */
  /* -------------------------------------------------------------------------- */
  // region updateHousesFlatsSummary
  updateHousesFlatsSummary() {

    const houseSummary = this.dashboardService.dashboard?.houseSummary;

    this.housesTotal = houseSummary?.totalHousesFlats ?? 0;
    this.housesActive = houseSummary?.activeHousesFlats ?? 0;
    this.housesOverdue = houseSummary?.overdueHousesFlats ?? 0;

    this.housesActivePercentage = this.housesTotal
      ? ((this.housesActive / this.housesTotal) * 100).toFixed(1)
      : '0.0';

    this.housesOverduePercentage = this.housesTotal
      ? ((this.housesOverdue / this.housesTotal) * 100).toFixed(1)
      : '0.0';

    /* Se usan las mismas etiquetas que las tarjetas KPI para que un predio no se
       llame de dos formas distintas dentro de la misma sección. */
    this.housesDistribution = [
      {
        name: 'Activos',
        value: this.housesActive,
        percentage: this.housesActivePercentage,
        color: this.positiveColor
      },
      {
        name: 'Inactivos',
        value: this.housesOverdue,
        percentage: this.housesOverduePercentage,
        color: this.negativeColor
      }
    ];

    this.hasHousesData = this.housesActive + this.housesOverdue > 0;

    if (!this.hasHousesData) {
      this.housesFlatsSummaryOption = {};
      this.housesChartAriaLabel = '';
      return;
    }

    this.housesChartAriaLabel = 'Gráfica de dona con la distribución de predios de la colonia. '
      + `Total ${this.housesTotal} predios. `
      + `Activos: ${this.housesActive} (${this.housesActivePercentage}%). `
      + `Inactivos: ${this.housesOverdue} (${this.housesOverduePercentage}%).`;

    this.housesFlatsSummaryOption = this.buildDonutOptions(
      this.housesDistribution,
      'Predios'
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                      ANCHOR updateCurrentMonthSummary                      */
  /* -------------------------------------------------------------------------- */
  // region updateCurrentMonthSummary
  /** Expone las cifras mensuales que el endpoint ya devolvía y no se mostraban. */
  updateCurrentMonthSummary() {

    this.setMonthLabels();

    this.visitsCurrentMonth = this.dashboardService.dashboard?.visitsCurrentMonth ?? 0;
    this.visitsLastMonth = this.dashboardService.dashboard?.visitsLastMonth ?? 0;

    /* El backend entrega una razón (mes actual / mes anterior), no un delta:
       0.9 significa 10% menos visitas que el mes pasado. */
    const monthRatio = this.dashboardService.dashboard?.currentMonthVsLastMonth ?? 0;

    if (!this.visitsLastMonth) {
      this.monthVariationValue = '—';
      this.monthVariationText = `Sin visitas en ${this.lastMonthLabel} para comparar`;
      this.monthVariationDirection = 'none';
      this.monthVariationIcon = 'remove';
      return;
    }

    const variation = Math.round((monthRatio - 1) * 1000) / 10;

    if (!variation) {
      this.monthVariationValue = '0%';
      this.monthVariationText = 'Sin cambios respecto al mes anterior';
      this.monthVariationDirection = 'flat';
      this.monthVariationIcon = 'remove';
      return;
    }

    if (variation > 0) {
      this.monthVariationValue = `+${variation.toFixed(1)}%`;
      this.monthVariationText = 'Más visitas que el mes anterior';
      this.monthVariationDirection = 'up';
      this.monthVariationIcon = 'arrow_upward';
      return;
    }

    this.monthVariationValue = `${variation.toFixed(1)}%`;
    this.monthVariationText = 'Menos visitas que el mes anterior';
    this.monthVariationDirection = 'down';
    this.monthVariationIcon = 'arrow_downward';

  }

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR updateReportsSummary                         */
  /* -------------------------------------------------------------------------- */
  // region updateReportsSummary
  /** Normaliza el endpoint de insights para que el HTML solo pinte valores. */
  updateReportsSummary() {

    const insights = this.reportService.insights;

    if (!insights) {
      this.resetReportsSummary();
      return;
    }

    this.reportsTotal = insights.total;
    this.reportsPendings = insights.pendings;
    this.reportsInProgress = insights.inProgress;
    this.reportsResolved = insights.resolved;
    this.reportsCanceled = insights.canceled;

    this.reportsPendingsPercentage = insights.pendingsPercentage;
    this.reportsInProgressPercentage = insights.inProgressPercentage;
    this.reportsResolvedPercentage = insights.resolvedPercentage;
    this.reportsCanceledPercentage = insights.canceledPercentage;

    this.reportsMonthTotal = insights.monthTotal;
    this.reportsAverageResolutionLabel = this.buildAverageResolutionLabel(
      insights.averageResolutionHours
    );

    this.setReportsMonthVariation(
      insights.monthVariationDirection,
      insights.monthVariationPercentage
    );

    this.reportsDistribution = insights.statusSummary.map(
      item => ({
        name: item.name,
        value: item.count,
        percentage: item.percentage,
        color: this.getReportStatusColor(item.status)
      })
    );

    this.reportsMonthlyRows = insights.monthlySummary;

    this.latestReports = insights.latest.map(
      report => this.buildReportListItem(report)
    );

    this.hasLatestReports = this.latestReports.length > 0;
    this.hasReportsData = this.reportsTotal > 0;
    this.hasReportsMonthlyData = insights.monthlySummary.some(
      item => item.total > 0
    );

    this.setReportsStatusDonut();
    this.setReportsMonthlyChart();

  }

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR REPORTS PRIVATE HELPERS                      */
  /* -------------------------------------------------------------------------- */

  /** Dona de composición por estado, con el mismo builder que Predios. */
  private setReportsStatusDonut() {

    if (!this.hasReportsData) {
      this.reportsStatusDonutOption = {};
      this.reportsDonutAriaLabel = '';
      return;
    }

    const breakdown = this.reportsDistribution
      .map(item => `${item.name}: ${item.value} (${item.percentage}%)`)
      .join(', ');

    this.reportsDonutAriaLabel = 'Gráfica de dona con la distribución de reportes por estado. '
      + `Total ${this.reportsTotal} reportes. ${breakdown}.`;

    this.reportsStatusDonutOption = this.buildDonutOptions(
      this.reportsDistribution,
      'Reportes'
    );

  }

  /** Barras apiladas con los reportes de los últimos 6 meses por estado. */
  private setReportsMonthlyChart() {

    if (!this.hasReportsMonthlyData) {
      this.reportsMonthlyOption = {};
      this.reportsMonthlyAriaLabel = '';
      return;
    }

    const stackedSeries = [
      {
        name: 'Pendientes',
        data: this.reportsMonthlyRows.map(item => item.pendings)
      },
      {
        name: 'En progreso',
        data: this.reportsMonthlyRows.map(item => item.inProgress)
      },
      {
        name: 'Resueltos',
        data: this.reportsMonthlyRows.map(item => item.resolved)
      },
      {
        /* Los cancelados van al final de la pila: son el cierre que no resolvió
           nada, y así la punta redondeada de la columna sigue cayendo sobre la
           última serie. La dona de distribución NO los incluye: esa gráfica
           reparte los reportes por estado, y cancelado no es un estado. */
        name: 'Cancelados',
        data: this.reportsMonthlyRows.map(item => item.canceled)
      }
    ];

    const monthlyTotal = this.reportsMonthlyRows.reduce(
      (accumulated, item) => accumulated + item.total,
      0
    );

    this.reportsMonthlyAriaLabel = 'Gráfica de barras apiladas con los reportes por mes de los últimos 6 meses, '
      + `separados por estado. Total ${monthlyTotal} reportes.`;

    this.reportsMonthlyOption = {
      color: [
        this.reportPendingColor,
        this.reportProgressColor,
        this.reportResolvedColor,
        this.reportCanceledColor
      ],
      aria: { enabled: true },
      animationDuration: 300,
      textStyle: this.chartTextStyle,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...this.chartTooltipStyle,
        formatter: (params: any) => this.buildStackedTooltip(
          params,
          'Total de reportes',
          'Sin reportes en este mes'
        )
      },
      legend: this.chartLegendStyle,
      grid: {
        top: 16,
        left: 8,
        right: 8,
        bottom: 34,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.reportsMonthlyRows.map(item => item.label),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        axisLabel: {
          color: '#475569',
          fontSize: 11,
          hideOverlap: true
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#475569',
          fontSize: 11
        },
        splitLine: {
          lineStyle: {
            color: '#E2E8F0',
            type: 'dashed'
          }
        }
      },
      series: stackedSeries.map((serie, index) => ({
        name: serie.name,
        type: 'bar',
        stack: 'reportes',
        barMaxWidth: 28,
        // Solo la última serie de la pila redondea la punta de la columna.
        itemStyle: index === stackedSeries.length - 1
          ? { borderRadius: [3, 3, 0, 0] }
          : {},
        label: {
          show: true,
          position: 'inside',
          fontSize: 10,
          fontWeight: 600,
          color: '#FFFFFF',
          formatter: (params: any) => params.value > 0
            ? this.numberFormatter.format(params.value)
            : ''
        },
        labelLayout: { hideOverlap: true },
        emphasis: { focus: 'series' },
        data: serie.data
      })) as any
    };

  }

  /** Deja los indicadores de reportes en cero cuando no hay respuesta útil. */
  private resetReportsSummary() {

    this.reportsTotal = 0;
    this.reportsPendings = 0;
    this.reportsInProgress = 0;
    this.reportsResolved = 0;
    this.reportsCanceled = 0;
    this.reportsPendingsPercentage = '0.0';
    this.reportsInProgressPercentage = '0.0';
    this.reportsResolvedPercentage = '0.0';
    this.reportsCanceledPercentage = '0.0';
    this.reportsMonthTotal = 0;
    this.reportsAverageResolutionLabel = 'Sin resoluciones registradas';
    this.reportsDistribution = [];
    this.reportsMonthlyRows = [];
    this.latestReports = [];
    this.hasReportsData = false;
    this.hasReportsMonthlyData = false;
    this.hasLatestReports = false;
    this.reportsStatusDonutOption = {};
    this.reportsMonthlyOption = {};
    this.reportsDonutAriaLabel = '';
    this.reportsMonthlyAriaLabel = '';
    this.reportsMonthVariationValue = '—';
    this.reportsMonthVariationText = 'Sin reportes del mes anterior';
    this.reportsMonthVariationDirection = 'none';
    this.reportsMonthVariationIcon = 'remove';

  }

  /** Traduce la variación mensual que ya calcula el backend a texto e icono. */
  private setReportsMonthVariation(
    direction: MonthVariationDirection,
    percentage?: string
  ) {

    if (direction === 'none' || percentage === undefined) {
      this.reportsMonthVariationValue = '—';
      this.reportsMonthVariationText = `Sin reportes en ${this.lastMonthLabel} para comparar`;
      this.reportsMonthVariationDirection = 'none';
      this.reportsMonthVariationIcon = 'remove';
      return;
    }

    const variation = Math.abs(Number(percentage) || 0);

    if (direction === 'flat' || !variation) {
      this.reportsMonthVariationValue = '0%';
      this.reportsMonthVariationText = 'Sin cambios respecto al mes anterior';
      this.reportsMonthVariationDirection = 'flat';
      this.reportsMonthVariationIcon = 'remove';
      return;
    }

    if (direction === 'up') {
      this.reportsMonthVariationValue = `+${variation.toFixed(1)}%`;
      this.reportsMonthVariationText = 'Más reportes que el mes anterior';
      this.reportsMonthVariationDirection = 'up';
      this.reportsMonthVariationIcon = 'arrow_upward';
      return;
    }

    this.reportsMonthVariationValue = `-${variation.toFixed(1)}%`;
    this.reportsMonthVariationText = 'Menos reportes que el mes anterior';
    this.reportsMonthVariationDirection = 'down';
    this.reportsMonthVariationIcon = 'arrow_downward';

  }

  /** Promedio de resolución en la unidad que se lee mejor (horas o días). */
  private buildAverageResolutionLabel(
    averageResolutionHours?: number
  ): string {

    if (averageResolutionHours === undefined) {
      return 'Sin resoluciones registradas';
    }

    if (averageResolutionHours < 24) {
      return `${averageResolutionHours.toFixed(1)} h en promedio para resolver`;
    }

    const days = averageResolutionHours / 24;

    return `${days.toFixed(1)} días en promedio para resolver`;
  }

  /** Color de la serie de un estado de reporte (-1 pendiente, 0 en proceso). */
  private getReportStatusColor(
    status: number
  ): string {

    switch (status) {
      case -1: return this.reportPendingColor;
      case 0: return this.reportProgressColor;
      default: return this.reportResolvedColor;
    }
  }

  /** Icono Material del estado, siempre acompañado del texto del chip. */
  private getReportStatusIcon(
    status: number
  ): string {

    switch (status) {
      case -1: return 'pending_actions';
      case 0: return 'autorenew';
      default: return 'task_alt';
    }
  }

  /** Modificador BEM del chip de estado. */
  private getReportStatusClass(
    status: number
  ): string {

    switch (status) {
      case -1: return 'neighborhoodPage__statusChipPending';
      case 0: return 'neighborhoodPage__statusChipProgress';
      default: return 'neighborhoodPage__statusChipResolved';
    }
  }

  /** Etiqueta corta del estado para el chip de la lista. */
  private getReportStatusName(
    status: number
  ): string {

    switch (status) {
      case -1: return 'Pendiente';
      case 0: return 'En progreso';
      default: return 'Resuelto';
    }
  }

  /** Ubicación legible del reporte: predio, calle o amenidad. */
  private getReportLocationLabel(
    report: ReportLatest
  ): string {

    const streetName = report.streetBuildingName;
    const houseNumber = report.houseFlatNumber;

    if (streetName && houseNumber) return `${streetName} ${houseNumber}`;
    if (houseNumber) return `Predio ${houseNumber}`;
    if (streetName) return streetName;
    if (report.amenityName) return report.amenityName;

    return 'Sin ubicación';
  }

  /** Aplana un reporte a la fila que consume la lista de últimos reportes. */
  private buildReportListItem(
    report: ReportLatest
  ): IReportListItem {

    const createdAt = moment.utc(report.createdAt)
      .add(this.visitsTimeZone, 'hours')
      .locale('es');

    return {
      reportId: report.reportId,
      title: report.title,
      statusName: this.getReportStatusName(report.status),
      statusIcon: this.getReportStatusIcon(report.status),
      statusClass: this.getReportStatusClass(report.status),
      locationLabel: this.getReportLocationLabel(report),
      dateLabel: createdAt.isValid()
        ? createdAt.format('DD MMM YYYY, HH:mm')
        : '',
      dateTime: report.createdAt
    };
  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR VISITS PRIVATE HELPERS                     */
  /* -------------------------------------------------------------------------- */

  /** Calcula total, desglose por tipo, hora pico, tabla accesible y aria-label. */
  private setVisitsSummaries(
    visitsTodaySummary: VisitTodaySummary[],
    types: string[],
    colors: string[]
  ) {

    this.totalVisits = visitsTodaySummary.reduce(
      (accumulated, item) => accumulated + item.visitCount,
      0
    );

    this.visitTypeSummaries = types.map((typeName, index) => {

      const count = visitsTodaySummary.reduce(
        (sum, hourData) => sum + (hourData.types.find(item => item.name === typeName)?.count ?? 0),
        0
      );

      return {
        name: typeName,
        count,
        percentage: this.totalVisits
          ? ((count / this.totalVisits) * 100).toFixed(1)
          : '0.0',
        color: colors[index]
      };
    });

    this.hourlyTableRows = visitsTodaySummary.map(
      item => ({
        hour: item.hourLabel,
        counts: types.map(
          typeName => item.types.find(type => type.name === typeName)?.count ?? 0
        ),
        total: item.visitCount
      })
    );

    const peakHour = visitsTodaySummary.reduce<VisitTodaySummary | undefined>(
      (max, item) => !max || item.visitCount > max.visitCount ? item : max,
      undefined
    );

    if (!peakHour || !peakHour.visitCount) {
      this.peakHourLabel = undefined;
      this.peakHourCount = 0;
    } else {
      this.peakHourLabel = peakHour.hourLabel;
      this.peakHourCount = peakHour.visitCount;
    }

    const breakdown = this.visitTypeSummaries
      .map(summary => `${summary.name}: ${summary.count}`)
      .join(', ');

    this.chartAriaLabel = `Gráfica de barras apiladas con las visitas por hora del ${this.selectedDateLabel}. `
      + `Total ${this.totalVisits} visitas. ${breakdown}.`;
  }

  /** Limpia los indicadores de visitas cuando el día no tiene información. */
  private resetVisitsSummaries() {
    this.totalVisits = 0;
    this.visitTypeSummaries = [];
    this.hourlyTableRows = [];
    this.peakHourLabel = undefined;
    this.peakHourCount = 0;
    this.chartAriaLabel = '';
    this.visitsTodaySummaryChartOptions = undefined;
  }

  /** Resuelve el color de la serie de un tipo, con fallback por índice. */
  private getSeriesColor(
    typeName: string,
    index: number
  ): string {

    const normalized = typeName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    return this.seriesColorByType[normalized]
      ?? this.seriesColorPalette[index % this.seriesColorPalette.length];
  }

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR SHARED CHART BUILDERS                        */
  /* -------------------------------------------------------------------------- */

  /** Dona con la misma paleta, leyenda y tooltip que el resto de la pantalla. */
  private buildDonutOptions(
    items: IChartItemSummary[],
    tooltipHeader: string
  ): EChartsOption {

    return {
      color: items.map(item => item.color),
      aria: { enabled: true },
      animationDuration: 300,
      textStyle: this.chartTextStyle,
      tooltip: {
        trigger: 'item',
        ...this.chartTooltipStyle,
        formatter: (params: any) => this.buildItemTooltip(
          params,
          tooltipHeader
        )
      },
      legend: this.chartLegendStyle,
      series: [
        {
          name: tooltipHeader,
          type: 'pie',
          radius: ['48%', '72%'],
          center: ['50%', '46%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderColor: '#FFFFFF',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 18,
              fontWeight: 'bold',
              color: '#0F172A'
            }
          },
          labelLine: { show: false },
          data: items.map(
            item => ({
              name: item.name,
              value: item.value
            })
          )
        }
      ]
    };
  }

  /** Tooltip de un solo ítem (donas), con la etiqueta y el valor separados. */
  private buildItemTooltip(
    params: any,
    header: string
  ): string {

    const value = Number(params.value) || 0;

    return `
      <div style="font-weight:600;color:#0F172A;margin-bottom:6px;">${header}</div>
      <div style="display:flex;align-items:center;gap:16px;min-width:200px;">
        <span style="display:flex;align-items:center;gap:8px;color:#334155;">
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${params.color};"></span>
          <span>${params.name}</span>
        </span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">
          ${this.numberFormatter.format(value)} &middot; ${params.percent}%
        </span>
      </div>
    `;
  }

  /** Tooltip de eje (barras) con el peso porcentual sobre el total del periodo. */
  private buildAxisTooltip(
    params: any,
    total: number
  ): string {

    const paramArray = Array.isArray(params) ? params : [params];
    const item = paramArray[0];

    if (!item) return '';

    const value = Number(item.value) || 0;
    const percent = total
      ? ((value / total) * 100).toFixed(1)
      : '0.0';

    return `
      <div style="font-weight:600;color:#0F172A;margin-bottom:6px;">${item.axisValue}</div>
      <div style="display:flex;align-items:center;gap:16px;min-width:200px;">
        <span style="display:flex;align-items:center;gap:8px;color:#334155;">
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${item.color};"></span>
          <span>Entradas</span>
        </span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">
          ${this.numberFormatter.format(value)} &middot; ${percent}%
        </span>
      </div>
    `;
  }

  /** Etiquetas del mes actual y del anterior, ej. "Agosto de 2026". */
  private setMonthLabels() {

    const currentMonth = moment.utc()
      .add(this.visitsTimeZone, 'hours')
      .locale('es');

    const lastMonth = currentMonth.clone().subtract(1, 'month');

    this.currentMonthLabel = this.capitalize(
      currentMonth.format('MMMM [de] YYYY')
    );

    this.lastMonthLabel = this.capitalize(
      lastMonth.format('MMMM [de] YYYY')
    );
  }

  /** Pone en mayúscula la primera letra de una etiqueta localizada. */
  private capitalize(
    value: string
  ): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /** Etiqueta larga de la fecha seleccionada, ej. "Sábado 22 de agosto de 2026". */
  private setSelectedDateLabel() {

    if (!this.model) {
      this.selectedDateLabel = '';
      return;
    }

    const selectedDay = moment({
      year: this.model.year,
      month: this.model.month - 1,
      day: this.model.day
    }).locale('es');

    const label = selectedDay.format('dddd D [de] MMMM [de] YYYY');

    this.selectedDateLabel = label.charAt(0).toUpperCase() + label.slice(1);
  }

  /** Genera el HTML del tooltip con la etiqueta y el valor bien separados. */
  private buildVisitsTooltip(
    params: any
  ): string {

    return this.buildStackedTooltip(
      params,
      'Total de visitas',
      'Sin visitas en esta hora'
    );
  }

  /** Tooltip compartido por todas las barras apiladas de la pantalla. */
  private buildStackedTooltip(
    params: any,
    totalLabel: string,
    emptyLabel: string
  ): string {

    const paramArray = Array.isArray(params) ? params : [params];
    const axisValue = paramArray[0]?.axisValue ?? '';
    const total = paramArray.reduce(
      (sum: number, item: any) => sum + (Number(item.value) || 0),
      0
    );

    let tooltipHtml = `<div style="font-weight:600;color:#0F172A;margin-bottom:6px;">${axisValue}</div>`;

    if (!total) {
      tooltipHtml += `<div style="color:#475569;">${emptyLabel}</div>`;
      return tooltipHtml;
    }

    tooltipHtml += `
      <div style="display:flex;align-items:center;gap:16px;color:#475569;margin-bottom:6px;">
        <span>${totalLabel}</span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;">${this.numberFormatter.format(total)}</span>
      </div>
    `;

    for (const item of paramArray) {

      const value = Number(item.value) || 0;

      // Solo se listan los tipos con visitas para no saturar el tooltip.
      if (!value) continue;

      const percent = ((value / total) * 100).toFixed(1);

      tooltipHtml += `
        <div style="display:flex;align-items:center;gap:16px;min-width:200px;margin-top:2px;">
          <span style="display:flex;align-items:center;gap:8px;color:#334155;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${item.color};"></span>
            <span>${item.seriesName}</span>
          </span>
          <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">${this.numberFormatter.format(value)} &middot; ${percent}%</span>
        </div>
      `;
    }

    return tooltipHtml;
  }

}
